/**
 * PrintNZ uploader — the printnz.photos order app as a Shopify theme section.
 * Ported from printnz-cloud/public/app.js; markup is sections/printnz-uploader.liquid.
 *
 * Flow:
 *   1. User adds photos → prepare-upload on Build4 allocates an order + presigned URLs
 *   2. Each photo PUTs directly to R2
 *   3. Place order → /print-orders/complete
 *   4. Build4 downloads from R2, generates contact sheet, prints
 *
 * Fulfilment: "instore" (pay at the counter), or pay online via Shopify and
 * "collect" / "ship". For online orders /complete returns a checkout_url (a
 * Shopify draft-order invoice) and we redirect there; Build4 holds the photos
 * until Shopify reports the order paid.
 *
 * Pricing tables must stay in sync with Build4 modules/print-pricing.ts — the
 * server charges its own figure for online orders.
 */
(function () {
  'use strict';

  // ─── Fixed-size pricing ────────────────────────────────────────────────────
  // Quantity breaks are the till's (BPRO STOCK.BREAK_Qn/BREAK_Dn on T46, T57,
  // T68, T810, T812 — best reached tier wins), so online and the counter charge
  // the same. They're also listed on the printing page and product descriptions.
  const pricingTiers = {
    '6x4': [
      { min: 1, max: 19, price: 1.50 },
      { min: 20, max: 49, price: 0.80 },
      { min: 50, max: 99, price: 0.60 },
      { min: 100, max: Infinity, price: 0.40 }
    ],
    '5x7': [
      { min: 1, max: 29, price: 2.50 },
      { min: 30, max: Infinity, price: 1.00 }
    ],
    '6x8': [
      { min: 1, max: 4, price: 7.00 },
      { min: 5, max: 9, price: 4.90 },
      { min: 10, max: Infinity, price: 4.20 }
    ],
    '8x10': [
      { min: 1, max: 4, price: 12.00 },
      { min: 5, max: 9, price: 8.40 },
      { min: 10, max: Infinity, price: 6.00 }
    ],
    '8x12': [
      { min: 1, max: 4, price: 15.00 },
      { min: 5, max: 9, price: 10.50 },
      { min: 10, max: Infinity, price: 7.50 }
    ]
  };

  const pricing = {
    'passport': 15.00,
    '6x4': 1.50,
    '5x7': 2.50,
    '6x8': 7.00,
    '8x10': 12.00,
    '8x12': 15.00,
    'A4': 15.00,
    'A3': 37.50,
    'A2': 55.00,
    'A1': 75.00
  };

  // Sizes where each line picks its own paper instead of the global gloss/matt
  // finish. The paper key is sent as the line's `finish`, which also tells the
  // shop which printer it runs on: glossy/matte → Fujifilm, Ilford → Epson.
  // A3–A1 match the store's product variants. A4 can go either way: Fuji
  // gloss/matt, or Epson on Ilford (rung up in the till as 8x12/T812 at the
  // fine art price). Must match PRICES in Build4 print-pricing.ts.
  const PAPER_LABELS = {
    glossy:          'Fujifilm Gloss',
    matte:           'Fujifilm Matt',
    luster:          'Ilford Luster',
    cotton_smooth:   'Ilford Smooth Cotton Rag',
    cotton_textured: 'Ilford Textured Cotton Rag',
  };
  const PAPER_PRICES = {
    'A4': { glossy: 15.00, matte: 15.00, luster: 30.00, cotton_smooth: 35.00, cotton_textured: 35.00 },
    'A3': { luster: 37.50, cotton_smooth: 42.50, cotton_textured: 42.50 },
    'A2': { luster: 55.00, cotton_smooth: 65.00, cotton_textured: 65.00 },
    'A1': { luster: 75.00, cotton_smooth: 85.00, cotton_textured: 85.00 },
  };
  // Too big for the print envelope — posted in a tube.
  const TUBE_SIZES = new Set(['A3', 'A2', 'A1']);
  function hasPaperChoice(size) { return Object.prototype.hasOwnProperty.call(PAPER_PRICES, size); }
  function papersFor(size) { return Object.keys(PAPER_PRICES[size] || {}); }
  // Keep line.paper valid for its size (first listed paper otherwise).
  function normalizePaper(line) {
    const papers = papersFor(line.size);
    if (papers.length && !papers.includes(line.paper)) line.paper = papers[0];
  }

  const sizeOrder = ['6x4', '5x7', '6x8', '8x10', '8x12', 'A4', 'A3', 'A2', 'A1', 'custom', 'passport'];
  const sizeLabels = { passport: 'Passport Photo', custom: 'Custom Poster' };
  function sizeLabel(s) { return sizeLabels[s] || s; }

  // ─── Custom Poster pricing ─────────────────────────────────────────────────
  // In-house Poster Price Calculator (xlsx), in inches:
  //   RRP = ((coef × H) + (W / 24) × 0.11 × H) × 7 + flat
  // Customers enter cm; we convert before pricing.
  const POSTER_PAPERS = {
    luster:          { label: 'Ilford Luster',   coef: 0.26, flat: 10 },
    cotton_smooth:   { label: 'Cotton Smooth',   coef: 0.41, flat: 15 },
    cotton_textured: { label: 'Cotton Textured', coef: 0.56, flat: 15 },
  };
  // Smaller (Fuji minilab) sizes use standard photo paper, no Ilford/Cotton.
  const FUJI_FINISHES = {
    glossy: { label: 'Glossy' },
    lustre: { label: 'Lustre' },
  };
  const ROLL_WIDTH_IN = 24;
  const CM_PER_IN = 2.54;
  const POSTER_W_MIN_CM = 1;
  const POSTER_W_MAX_CM = 60;
  const POSTER_H_MIN_CM = 1;
  const POSTER_H_MAX_CM = 243;
  // Fuji minilab tiers: short-side cutoff (inches) → flat price.
  const FUJI_TIERS = [
    { maxShortIn: 6,    price: 10 },
    { maxShortIn: 8.27, price: 15 },
  ];
  const FUJI_MAX_SHORT_IN = FUJI_TIERS[FUJI_TIERS.length - 1].maxShortIn;

  function cmToIn(cm) { return cm / CM_PER_IN; }
  function getPaperDef(paper) { return POSTER_PAPERS[paper] || FUJI_FINISHES[paper] || null; }
  function defaultPaperFor(fuji) { return fuji ? 'glossy' : 'luster'; }

  function isFujiSpec(p) {
    const h = cmToIn(Number(p.height_cm));
    const w = cmToIn(Number(p.width_cm));
    if (!isFinite(h) || !isFinite(w)) return false;
    return Math.min(h, w) < FUJI_MAX_SHORT_IN;
  }

  function isPosterSpecValid(p) {
    if (!p) return false;
    const h = Number(p.height_cm), w = Number(p.width_cm);
    if (!isFinite(h) || !isFinite(w)) return false;
    if (h < POSTER_H_MIN_CM || h > POSTER_H_MAX_CM) return false;
    if (w < POSTER_W_MIN_CM || w > POSTER_W_MAX_CM) return false;
    if (!getPaperDef(p.paper)) return false;
    // Paper must match the printer this size routes to.
    const fuji = isFujiSpec(p);
    if (fuji && !FUJI_FINISHES[p.paper]) return false;
    if (!fuji && !POSTER_PAPERS[p.paper]) return false;
    return true;
  }

  function calculatePosterUnitPrice(p) {
    if (!isPosterSpecValid(p)) return 0;
    const h = cmToIn(Number(p.height_cm));
    const w = cmToIn(Number(p.width_cm));
    const shortIn = Math.min(h, w);
    if (shortIn < FUJI_MAX_SHORT_IN) {
      const tier = FUJI_TIERS.find(t => shortIn <= t.maxShortIn);
      if (tier) return tier.price;
    }
    const paper = POSTER_PAPERS[p.paper];
    const cost = (paper.coef * h) + ((w / ROLL_WIDTH_IN) * 0.11) * h;
    return Math.round((cost * 7 + paper.flat) * 100) / 100;
  }

  // The till has no A4 code: Fuji A4 is rung up as 8x12 (T812), so it shares
  // 8x12's breaks and its count. Ilford A4 is fine art — own price, no breaks.
  // Must match tierKey in Build4 print-pricing.ts.
  function tierKey(size, paper) {
    return size === 'A4' && (paper === 'glossy' || paper === 'matte') ? '8x12' : size;
  }

  // `quantity` is the total for the line's tierKey across the whole order.
  function calculateTieredPrice(size, quantity, paper) {
    if (size === 'custom') return 0; // posters priced per-item, not via tiers
    const base = hasPaperChoice(size)
      ? (PAPER_PRICES[size][paper] ?? PAPER_PRICES[size][papersFor(size)[0]])
      : (pricing[size] || 0);
    const tiers = pricingTiers[tierKey(size, paper)];
    const tier = tiers && tiers.find(t => quantity >= t.min && quantity <= t.max);
    return tier ? tier.price : base;
  }

  // Must match SHIPPING_* in Build4 modules/printnz-checkout.ts.
  const SHIPPING_PRINTS = 6.50;
  const SHIPPING_POSTER_TUBE = 12.50;

  // Quantity is bounded 1–100. parseInt alone lets negatives through, which
  // would produce a negative line total and silently discount the order.
  function clampQty(v) {
    const n = parseInt(v, 10);
    if (!isFinite(n)) return 1;
    return Math.min(100, Math.max(1, n));
  }

  // ─── Allowed file types ─────────────────────────────────────────────────────
  const ALLOWED_MIME_TYPES = new Set([
    'image/jpeg', 'image/pjpeg',
    'image/png',
    'image/bmp', 'image/x-bmp', 'image/x-ms-bmp',
    'image/tiff', 'image/tiff-fx',
    'image/heic', 'image/heif',
  ]);
  const ALLOWED_EXTS = new Set(['jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff', 'heic', 'heif']);
  const MAX_CLIENT_BYTES = 100 * 1024 * 1024;

  function extOf(name) {
    const m = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
  }
  // MIME match OR extension match — mobile pickers (Android Google Photos
  // especially) often hand over file.type === "" for perfectly good JPEGs.
  function isAcceptedFile(file) {
    if (file.type && ALLOWED_MIME_TYPES.has(file.type.toLowerCase())) return true;
    return ALLOWED_EXTS.has(extOf(file.name));
  }

  // Browsers can only thumbnail some of these; TIFF/HEIC (outside Safari) get a placeholder.
  const PREVIEWABLE = new Set(['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp']);

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function money(n) { return '$' + n.toFixed(2); }

  // A failure is worth retrying when it's network-level (no status — includes
  // Cloudflare error pages, which lack CORS headers) or a 5xx. 4xx won't improve.
  function isTransientError(err) {
    if (err && typeof err.status === 'number') return err.status >= 500;
    return true;
  }

  // Retry schedule for /complete. A backend restart takes ~75s and we lost a
  // real order to one; these delays ride out ~95s. Photos are already in R2,
  // so retrying with the identical payload is cheap.
  const COMPLETE_RETRY_DELAYS_MS = [5000, 15000, 30000, 45000];

  function defaultPrintLine(size, paper) {
    const line = {
      size: size || '6x4',
      quantity: 1,
      paper: paper || null, // only used by sizes with a paper choice
      poster: { paper: 'luster', height_cm: 36, width_cm: 51 },
    };
    normalizePaper(line);
    return line;
  }

  function init(root) {
    // Theme editor re-renders and duplicate script tags must not double-bind.
    if (root.dataset.pnzReady) return;
    root.dataset.pnzReady = '1';
    const API_BASE = root.dataset.apiBase;
    const API_KEY = root.dataset.apiKey;
    // Size of the product page this was opened from (e.g. 5x7); 6x4 otherwise.
    const sizeFromPage = sizeOrder.find(s => s.toLowerCase() === String(root.dataset.defaultSize || '').toLowerCase());
    const DEFAULT_SIZE = sizeFromPage && sizeFromPage !== 'custom' ? sizeFromPage : '6x4';
    // Paper of the variant the page was opened on (A3–A1 cotton rag links).
    const DEFAULT_PAPER = root.dataset.defaultPaper;
    const $ = (name) => root.querySelector(`[data-pnz="${name}"]`);

    const el = {
      form: $('form'), drop: $('drop'), fileInput: $('file-input'),
      finishWrap: $('finish-wrap'), finish: $('finish'),
      progress: $('progress'), progressBar: $('progress-bar'), progressText: $('progress-text'),
      previews: $('previews'), more: $('more'), moreInput: $('more-input'),
      count: $('count'), subtotal: $('subtotal'), shippingRow: $('shipping-row'),
      shipping: $('shipping'), total: $('total'), shipPrice: $('ship-price'),
      name: $('name'), email: $('email'), notes: $('notes'),
      error: $('error'), submit: $('submit'), secure: $('secure'),
      done: $('done'), orderId: $('order-id'), newOrder: $('new-order'),
      dialog: $('dialog'), dialogBody: $('dialog-body'),
      dialogCancel: $('dialog-cancel'), dialogConfirm: $('dialog-confirm'),
      overlay: $('overlay'), overlayText: $('overlay-text'),
    };
    const fulfilmentInputs = root.querySelectorAll('.pnz-fulfil input[type="radio"]');

    if (root.dataset.customerName) el.name.value = root.dataset.customerName;
    if (root.dataset.customerEmail) el.email.value = root.dataset.customerEmail;

    // ─── State ────────────────────────────────────────────────────────────────
    let uploadedFiles = [];
    let backgroundUploads = {};
    let totalBytesToUpload = 0;
    let totalBytesUploaded = 0;
    let currentOrderId = null;
    let currentR2Prefix = null;
    let fileIndexCounter = 0;
    let prepareChain = Promise.resolve(); // serializes /prepare-upload calls (see handleFiles)

    function resetState() {
      uploadedFiles = [];
      backgroundUploads = {};
      currentOrderId = null;
      currentR2Prefix = null;
      fileIndexCounter = 0;
      totalBytesToUpload = 0;
      totalBytesUploaded = 0;
    }

    function getFulfilment() {
      const checked = root.querySelector('.pnz-fulfil input:checked');
      return checked ? checked.value : 'instore';
    }
    function isOnline(f) { return f === 'ship' || f === 'collect'; }

    // A3–A1 and large-format posters ship in a tube; Fuji sizes fit the print envelope.
    // Must match needsTube in Build4 modules/printnz-checkout.ts.
    function shippingCost() {
      const needsTube = uploadedFiles.some(f => f.options.prints.some(line => TUBE_SIZES.has(line.size) ||
        (line.size === 'custom' && isPosterSpecValid(line.poster) && !isFujiSpec(line.poster))));
      return needsTube ? SHIPPING_POSTER_TUBE : SHIPPING_PRINTS;
    }
    function shippingFor(fulfilment) { return fulfilment === 'ship' ? shippingCost() : 0; }

    function showError(msg) {
      el.error.textContent = msg;
      el.error.hidden = !msg;
      if (msg) el.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // ─── API ──────────────────────────────────────────────────────────────────
    function apiCall(path, body) {
      return fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify(body),
      }).then(res => {
        if (!res.ok) {
          // Error bodies aren't always JSON (Cloudflare serves HTML when the
          // origin is down), so read text and try to parse.
          return res.text().then(t => {
            let msg = '';
            try { msg = JSON.parse(t).error; } catch (e) { /* not JSON */ }
            const err = new Error(msg || `HTTP ${res.status}`);
            err.status = res.status;
            throw err;
          });
        }
        return res.json();
      });
    }

    // Prepare-upload only needs bytes/type for presigned URLs — print options
    // are decided in /complete. Dummy fields keep the server's PrepareFile happy.
    function buildFileDesc(f) {
      return { name: f.name, size: f.size, type: f.type || 'image/jpeg', print_size: '6x4', finish: 'glossy', qty: 1 };
    }

    // ─── Progress ─────────────────────────────────────────────────────────────
    function ongoingCount() {
      return Object.values(backgroundUploads).filter(u => u.status === 'uploading').length;
    }

    function updateOverallProgress() {
      el.progress.hidden = uploadedFiles.length === 0;
      if (totalBytesToUpload <= 0) {
        el.progressBar.style.width = '0%';
        el.progressText.textContent = '';
        updateSubmitLabel();
        return;
      }
      const percent = Math.max(0, Math.min(Math.round((totalBytesUploaded / totalBytesToUpload) * 100), 100));
      el.progressBar.style.width = percent + '%';
      const ongoing = ongoingCount();
      const completed = uploadedFiles.filter(f => f.uploadStatus === 'completed').length;
      el.progress.classList.toggle('is-done', percent === 100 && ongoing === 0);
      if (percent === 100 && ongoing === 0) {
        el.progressText.textContent = 'All photos uploaded';
      } else if (ongoing > 0) {
        el.progressText.textContent = `Uploading ${ongoing} of ${uploadedFiles.length} photos (${percent}%)`;
      } else {
        el.progressText.textContent = `Uploaded ${completed} of ${uploadedFiles.length} photos`;
      }
      updateSubmitLabel();
    }

    function updateSubmitLabel() {
      const ongoing = ongoingCount();
      if (ongoing > 0) {
        el.submit.textContent = `Uploading… (${ongoing} to go)`;
      } else {
        el.submit.textContent = isOnline(getFulfilment()) ? 'Continue to payment' : 'Place order';
      }
    }

    // ─── File handling ────────────────────────────────────────────────────────
    el.drop.addEventListener('dragover', (e) => { e.preventDefault(); el.drop.classList.add('is-over'); });
    el.drop.addEventListener('dragleave', () => el.drop.classList.remove('is-over'));
    el.drop.addEventListener('drop', (e) => {
      e.preventDefault();
      el.drop.classList.remove('is-over');
      handleFiles(e.dataTransfer.files);
    });
    [el.fileInput, el.moreInput].forEach(input => input.addEventListener('change', function () {
      if (this.files && this.files.length > 0) { handleFiles(this.files); this.value = ''; }
    }));

    function handleFiles(files) {
      if (!files || files.length === 0) return;
      showError('');

      const fileArray = Array.from(files);
      const rejected = fileArray.filter(f => !isAcceptedFile(f));
      const tooBig = fileArray.filter(f => isAcceptedFile(f) && f.size > MAX_CLIENT_BYTES);
      const imageFiles = fileArray.filter(f => isAcceptedFile(f) && f.size <= MAX_CLIENT_BYTES);

      const problems = [];
      if (rejected.length) {
        const names = rejected.map(f => f.name).slice(0, 5).join(', ');
        const more = rejected.length > 5 ? ` and ${rejected.length - 5} more` : '';
        problems.push(`${rejected.length} file(s) aren't a supported format (${names}${more}). We accept JPG, PNG, BMP, TIFF and iPhone HEIC photos.`);
      }
      if (tooBig.length) problems.push(`${tooBig.length} file(s) are over 100MB: ${tooBig.map(f => f.name).slice(0, 5).join(', ')}.`);
      if (problems.length) showError(problems.join(' '));
      if (imageFiles.length === 0) return;

      const newFiles = [];
      imageFiles.forEach(file => {
        if (uploadedFiles.some(f => f.name === file.name && f.size === file.size)) return;
        totalBytesToUpload += file.size;
        file.id = Date.now() + Math.random().toString(36).slice(2, 11);
        file.options = { prints: [defaultPrintLine(DEFAULT_SIZE, DEFAULT_PAPER)] };
        file.uploadStatus = 'pending';
        file.fileIndex = fileIndexCounter++;
        uploadedFiles.push(file);
        newFiles.push(file);
        createPreviewElement(file);
      });
      if (newFiles.length === 0) return;

      const prepareFiles = newFiles.map(buildFileDesc);
      // Capture this batch's start index now — fileIndexCounter keeps moving.
      const batchStartIndex = newFiles[0].fileIndex;
      const firstBatch = uploadedFiles.length === newFiles.length;

      // Serialize prepare-upload calls. Two batches racing would both see
      // currentOrderId === null and get two separate orders, orphaning the
      // first batch's files under an order /complete never references.
      prepareChain = prepareChain.then(() => {
        const prepareBody = { files: prepareFiles };
        if (currentOrderId) {
          prepareBody.order_id = currentOrderId;
          prepareBody.file_index_start = batchStartIndex;
        }
        return apiCall('/print-orders/prepare-upload', prepareBody).then(data => {
          if (!currentOrderId) {
            currentOrderId = data.order_id;
            currentR2Prefix = data.r2_prefix;
          }
          data.upload_urls.forEach((urlInfo, i) => {
            const file = newFiles[i];
            if (!file || !uploadedFiles.includes(file)) return; // removed while preparing
            file.r2Key = urlInfo.r2_key;
            file.uploadStatus = 'uploading';
            uploadFileToR2(file, urlInfo.put_url);
          });
        });
      }).catch(err => {
        console.error('Failed to prepare upload:', err);
        newFiles.forEach(f => markFileError(f.id, 'Could not start upload: ' + err.message));
      });

      updateOrderSummary();
      el.more.hidden = false;
      updateOverallProgress();

      if (firstBatch) {
        setTimeout(() => el.finishWrap.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
      }
    }

    // ─── Upload to R2 via presigned PUT ───────────────────────────────────────
    function cardFor(fileId) { return el.previews.querySelector(`[data-file-id="${fileId}"]`); }

    function setCardStatus(fileId, percent, text, state) {
      const card = cardFor(fileId);
      if (!card) return;
      const wrap = card.querySelector('.pnz-card__status');
      wrap.hidden = false;
      wrap.className = 'pnz-card__status' + (state ? ' is-' + state : '');
      wrap.querySelector('span').style.width = percent + '%';
      wrap.querySelector('p').textContent = text;
      if (state === 'done') setTimeout(() => { wrap.hidden = true; }, 2000);
    }

    function uploadFileToR2(file, putUrl) {
      const xhr = new XMLHttpRequest();
      backgroundUploads[file.id] = { xhr, status: 'uploading', bytesTotal: file.size, bytesUploaded: 0 };
      setCardStatus(file.id, 0, 'Uploading 0%');

      xhr.open('PUT', putUrl, true);
      xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
      // Flaky mobile networks can stop firing progress without ever erroring.
      xhr.timeout = 10 * 60 * 1000;

      xhr.upload.onprogress = function (e) {
        if (!e.lengthComputable) return;
        const bg = backgroundUploads[file.id];
        if (!bg) return;
        totalBytesUploaded += e.loaded - bg.bytesUploaded;
        bg.bytesUploaded = e.loaded;
        const pct = Math.round((e.loaded / e.total) * 100);
        setCardStatus(file.id, pct, `Uploading ${pct}%`);
        updateOverallProgress();
      };

      xhr.onload = function () {
        const bg = backgroundUploads[file.id];
        if (!bg) return;
        if (xhr.status >= 200 && xhr.status < 300) {
          file.uploadStatus = 'completed';
          bg.status = 'completed';
          totalBytesUploaded += bg.bytesTotal - bg.bytesUploaded;
          bg.bytesUploaded = bg.bytesTotal;
          setCardStatus(file.id, 100, 'Uploaded', 'done');
          updateOverallProgress();
        } else {
          markFileError(file.id, `Upload failed (${xhr.status})`);
        }
      };
      xhr.onerror = function () { markFileError(file.id, 'Network error — remove and re-add this photo'); };
      xhr.ontimeout = function () { markFileError(file.id, 'Upload timed out — check your connection'); };
      xhr.send(file);
    }

    // Sets BOTH the background status and file.uploadStatus so submit-time
    // checks see R2 and prepare failures alike.
    function markFileError(fileId, message) {
      if (backgroundUploads[fileId]) backgroundUploads[fileId].status = 'error';
      const file = uploadedFiles.find(f => f.id === fileId);
      if (file) file.uploadStatus = 'error';
      setCardStatus(fileId, 0, message, 'error');
      updateOverallProgress();
    }

    // ─── Photo cards ──────────────────────────────────────────────────────────
    function createPreviewElement(file) {
      const card = document.createElement('div');
      card.className = 'pnz-card';
      card.dataset.fileId = file.id;
      card.innerHTML = `
        <button type="button" class="pnz-card__remove" aria-label="Remove ${escapeHTML(file.name)}">&times;</button>
        <div class="pnz-card__thumb"><span>${escapeHTML(extOf(file.name).toUpperCase() || 'PHOTO')}</span></div>
        <p class="pnz-card__name">${escapeHTML(file.name)}</p>
        <div class="pnz-card__status" hidden><div class="pnz-card__bar"><span></span></div><p></p></div>
        <div class="pnz-lines"></div>
        <button type="button" class="pnz-card__add">+ Add another size</button>
      `;
      el.previews.appendChild(card);

      // Object URLs are instant and don't hold a base64 copy of a 30MB photo in memory.
      if (PREVIEWABLE.has(extOf(file.name)) || (file.type && /^image\/(jpeg|png|gif|webp|bmp)$/.test(file.type))) {
        const img = new Image();
        img.alt = '';
        img.decoding = 'async';
        img.onload = () => { const t = card.querySelector('.pnz-card__thumb'); t.innerHTML = ''; t.appendChild(img); };
        img.src = file.thumbUrl = URL.createObjectURL(file);
      }

      card.querySelector('.pnz-card__remove').addEventListener('click', () => removeFile(file.id));
      // Delegated — print lines are re-rendered on add/remove.
      card.addEventListener('change', handleLineChange);
      card.addEventListener('input', handleLineInput);
      card.addEventListener('click', handleLineClick);
      renderPrintLines(file.id);
    }

    function paperOptionsHTMLFor(line) {
      const fuji = isFujiSpec(line.poster);
      const set = fuji ? FUJI_FINISHES : POSTER_PAPERS;
      if (!set[line.poster.paper]) line.poster.paper = defaultPaperFor(fuji);
      return Object.keys(set).map(k =>
        `<option value="${k}"${k === line.poster.paper ? ' selected' : ''}>${set[k].label}</option>`).join('');
    }

    function paperOptionsHTML(file, idx, line) {
      if (!hasPaperChoice(line.size)) return '';
      return `<legend>Paper</legend>` + papersFor(line.size).map(k => `
            <label class="pnz-paper__opt">
              <input type="radio" name="pnz-paper-${file.id}-${idx}" value="${k}"${k === line.paper ? ' checked' : ''}>
              <span class="pnz-paper__name">${PAPER_LABELS[k]}</span>
              <span class="pnz-paper__price" data-paper="${k}">${money(PAPER_PRICES[line.size][k])}</span>
            </label>`).join('');
    }

    function buildPrintLineEl(file, idx) {
      const line = file.options.prints[idx];
      const sizeOptions = sizeOrder.map(s =>
        `<option value="${s}"${s === line.size ? ' selected' : ''}>${sizeLabel(s)}</option>`).join('');
      const wrapper = document.createElement('div');
      wrapper.className = 'pnz-line';
      wrapper.dataset.fileId = file.id;
      wrapper.dataset.lineIdx = String(idx);
      wrapper.innerHTML = `
        <div class="pnz-line__row">
          <select class="pnz-size" aria-label="Print size">${sizeOptions}</select>
          <input type="number" class="pnz-qty" aria-label="Quantity" value="${line.quantity}" min="1" max="100" inputmode="numeric">
          <button type="button" class="pnz-line__remove" aria-label="Remove this size"${file.options.prints.length === 1 ? ' hidden' : ''}>&times;</button>
        </div>
        <fieldset class="pnz-paper"${hasPaperChoice(line.size) ? '' : ' hidden'}>${paperOptionsHTML(file, idx, line)}</fieldset>
        <div class="pnz-poster"${line.size === 'custom' ? '' : ' hidden'}>
          <label>Height (cm)<input type="number" class="pnz-poster-h" min="${POSTER_H_MIN_CM}" max="${POSTER_H_MAX_CM}" step="1" value="${line.poster.height_cm}"></label>
          <label>Width (cm)<input type="number" class="pnz-poster-w" min="${POSTER_W_MIN_CM}" max="${POSTER_W_MAX_CM}" step="1" value="${line.poster.width_cm}"></label>
          <label>Finish<select class="pnz-poster-paper">${paperOptionsHTMLFor(line)}</select></label>
          <div class="pnz-poster__price"><span>Each</span><b class="pnz-poster-unit">—</b></div>
          <p class="pnz-poster__hint">Width ${POSTER_W_MIN_CM}–${POSTER_W_MAX_CM}cm, height ${POSTER_H_MIN_CM}–${POSTER_H_MAX_CM}cm. Smaller prints run on our photo printer: short side up to ${(FUJI_TIERS[0].maxShortIn * CM_PER_IN).toFixed(0)}cm $${FUJI_TIERS[0].price}, up to ${(FUJI_MAX_SHORT_IN * CM_PER_IN).toFixed(0)}cm $${FUJI_TIERS[1].price}.</p>
        </div>
      `;
      return wrapper;
    }

    function renderPrintLines(fileId) {
      const file = uploadedFiles.find(f => f.id === fileId);
      const card = cardFor(fileId);
      if (!file || !card) return;
      const container = card.querySelector('.pnz-lines');
      container.innerHTML = '';
      file.options.prints.forEach((_, idx) => container.appendChild(buildPrintLineEl(file, idx)));
      updatePosterUnitPrices(file);
    }

    function updatePosterUnitPrices(file) {
      const card = cardFor(file.id);
      if (!card) return;
      card.querySelectorAll('.pnz-line').forEach(lineEl => {
        const line = file.options.prints[parseInt(lineEl.dataset.lineIdx, 10)];
        const out = lineEl.querySelector('.pnz-poster-unit');
        if (!line || !out) return;
        if (hasPaperChoice(line.size)) {
          lineEl.querySelectorAll('.pnz-paper__price').forEach(p => {
            p.textContent = money(PAPER_PRICES[line.size][p.dataset.paper]);
          });
        }
        const price = line.size === 'custom' ? calculatePosterUnitPrice(line.poster) : 0;
        const bad = line.size === 'custom' && price <= 0;
        out.textContent = line.size !== 'custom' ? '—' : bad ? 'Size out of range' : money(price);
        out.classList.toggle('is-bad', bad);
      });
    }

    function lineCtx(target) {
      const lineEl = target.closest('.pnz-line');
      if (!lineEl) return null;
      const file = uploadedFiles.find(f => f.id === lineEl.dataset.fileId);
      if (!file) return null;
      const idx = parseInt(lineEl.dataset.lineIdx, 10);
      const line = file.options.prints[idx];
      return line ? { file, line, lineEl, idx } : null;
    }

    function handleLineChange(e) {
      const ctx = lineCtx(e.target);
      if (!ctx) return;
      if (e.target.matches('.pnz-size')) {
        ctx.line.size = e.target.value;
        ctx.lineEl.querySelector('.pnz-poster').hidden = ctx.line.size !== 'custom';
        // Each size has its own paper list (A4 adds the Fuji papers), so redraw it.
        normalizePaper(ctx.line);
        const picker = ctx.lineEl.querySelector('.pnz-paper');
        picker.hidden = !hasPaperChoice(ctx.line.size);
        picker.innerHTML = paperOptionsHTML(ctx.file, ctx.idx, ctx.line);
      } else if (e.target.matches('.pnz-paper input')) {
        ctx.line.paper = e.target.value;
      } else if (e.target.matches('.pnz-qty')) {
        ctx.line.quantity = clampQty(e.target.value);
        e.target.value = ctx.line.quantity;
      } else if (e.target.matches('.pnz-poster-paper')) {
        ctx.line.poster.paper = e.target.value;
      } else {
        return;
      }
      updatePosterUnitPrices(ctx.file);
      updateOrderSummary();
    }

    function handleLineInput(e) {
      const ctx = lineCtx(e.target);
      if (!ctx) return;
      if (e.target.matches('.pnz-poster-h') || e.target.matches('.pnz-poster-w')) {
        const key = e.target.matches('.pnz-poster-h') ? 'height_cm' : 'width_cm';
        ctx.line.poster[key] = parseFloat(e.target.value) || 0;
        ctx.lineEl.querySelector('.pnz-poster-paper').innerHTML = paperOptionsHTMLFor(ctx.line);
        updatePosterUnitPrices(ctx.file);
      } else if (e.target.matches('.pnz-qty')) {
        // Don't rewrite the field mid-keystroke; just keep the value sane.
        ctx.line.quantity = clampQty(e.target.value);
      } else {
        return;
      }
      updateOrderSummary();
    }

    function handleLineClick(e) {
      const removeBtn = e.target.closest('.pnz-line__remove');
      if (removeBtn) {
        const ctx = lineCtx(removeBtn);
        if (!ctx || ctx.file.options.prints.length <= 1) return;
        ctx.file.options.prints.splice(ctx.idx, 1);
        renderPrintLines(ctx.file.id);
        updateOrderSummary();
        return;
      }
      const addBtn = e.target.closest('.pnz-card__add');
      if (addBtn) {
        const file = uploadedFiles.find(f => f.id === addBtn.closest('.pnz-card').dataset.fileId);
        if (!file) return;
        file.options.prints.push(defaultPrintLine(DEFAULT_SIZE, DEFAULT_PAPER));
        renderPrintLines(file.id);
        updateOrderSummary();
      }
    }

    function removeFile(fileId) {
      const file = uploadedFiles.find(f => f.id === fileId);
      if (!file) return;
      // Remove the file's full contribution from BOTH totals so the overall
      // percentage stays consistent.
      const bg = backgroundUploads[fileId];
      if (bg) {
        if (bg.status === 'uploading' && bg.xhr) bg.xhr.abort();
        totalBytesUploaded -= bg.bytesUploaded || 0;
        delete backgroundUploads[fileId];
      }
      totalBytesToUpload -= file.size;
      if (file.thumbUrl) URL.revokeObjectURL(file.thumbUrl);

      uploadedFiles = uploadedFiles.filter(f => f.id !== fileId);
      const card = cardFor(fileId);
      if (card) card.remove();

      if (uploadedFiles.length === 0) {
        currentOrderId = null;
        currentR2Prefix = null;
        fileIndexCounter = 0;
        totalBytesToUpload = 0;
        totalBytesUploaded = 0;
        el.more.hidden = true;
      }
      updateOrderSummary();
      updateOverallProgress();
    }

    // ─── Order summary ────────────────────────────────────────────────────────
    function groupKey(line) { return hasPaperChoice(line.size) ? `${line.size}|${line.paper}` : line.size; }

    // Fixed-size lines are grouped across every photo so tiers apply to total
    // quantity, not per image. Posters are priced individually.
    function summarizeOrder() {
      const fixedGroups = {};
      const customLines = [];
      uploadedFiles.forEach(file => {
        file.options.prints.forEach(line => {
          const qty = line.quantity || 1;
          if (line.size === 'custom') {
            const unit = calculatePosterUnitPrice(line.poster);
            customLines.push({ file, line, unit, qty, lineTotal: unit * qty });
          } else {
            // Sizes with a paper choice group per paper (each has its own price).
            const key = groupKey(line);
            if (!fixedGroups[key]) fixedGroups[key] = { size: line.size, paper: hasPaperChoice(line.size) ? line.paper : null, qty: 0 };
            fixedGroups[key].qty += qty;
          }
        });
      });
      // Breaks are chosen from the total per tierKey (Fuji A4 counts with 8x12).
      const tierQty = {};
      Object.values(fixedGroups).forEach(grp => {
        const k = tierKey(grp.size, grp.paper);
        tierQty[k] = (tierQty[k] || 0) + grp.qty;
      });
      let subtotal = 0;
      Object.keys(fixedGroups).forEach(key => {
        const grp = fixedGroups[key];
        grp.unit = calculateTieredPrice(grp.size, tierQty[tierKey(grp.size, grp.paper)], grp.paper);
        grp.lineTotal = grp.unit * grp.qty;
        subtotal += grp.lineTotal;
      });
      customLines.forEach(c => { subtotal += c.lineTotal; });
      return { fixedGroups, customLines, subtotal };
    }

    function updateOrderSummary() {
      const { subtotal } = summarizeOrder();
      const fulfilment = getFulfilment();
      const shipping = shippingFor(fulfilment);
      el.count.textContent = uploadedFiles.length;
      el.subtotal.textContent = money(subtotal);
      el.shippingRow.hidden = fulfilment !== 'ship';
      el.shipping.textContent = money(shipping);
      el.shipPrice.textContent = '+' + money(shippingCost());
      el.total.textContent = money(subtotal + shipping);
      el.secure.hidden = !isOnline(fulfilment);
      el.dialogConfirm.textContent = isOnline(fulfilment) ? 'Pay now' : 'Place order';
      // Gloss/matt only applies to the photo-printer sizes, not posters or A3–A1.
      el.finishWrap.hidden = !uploadedFiles.some(f => f.options.prints.some(p => p.size !== 'custom' && !hasPaperChoice(p.size)));
      updateSubmitLabel();
    }

    fulfilmentInputs.forEach(input => input.addEventListener('change', updateOrderSummary));
    el.finish.addEventListener('change', updateOrderSummary);

    // ─── Confirm dialog ───────────────────────────────────────────────────────
    function renderConfirm() {
      const { fixedGroups, customLines, subtotal } = summarizeOrder();
      const fulfilment = getFulfilment();
      const shipping = shippingFor(fulfilment);
      const finishLabel = el.finish.value === 'matte' ? 'Matt' : 'Glossy';
      const rows = [];

      const paperOrder = Object.keys(PAPER_LABELS);
      Object.values(fixedGroups)
        .sort((a, b) => (sizeOrder.indexOf(a.size) - sizeOrder.indexOf(b.size)) || (paperOrder.indexOf(a.paper) - paperOrder.indexOf(b.paper)))
        .forEach(grp => {
          const paper = grp.paper ? PAPER_LABELS[grp.paper] : finishLabel;
          rows.push(`<tr><td><b>${escapeHTML(sizeLabel(grp.size))}</b> · ${escapeHTML(paper)}</td><td class="num">${grp.qty}</td><td class="num">${money(grp.unit)}</td><td class="num">${money(grp.lineTotal)}</td></tr>`);
        });
      customLines.forEach(c => {
        const paperLabel = (getPaperDef(c.line.poster.paper) || {}).label || c.line.poster.paper;
        rows.push(`<tr><td><b>Custom poster</b> · ${c.line.poster.height_cm}×${c.line.poster.width_cm}cm · ${escapeHTML(paperLabel)}</td><td class="num">${c.qty}</td><td class="num">${money(c.unit)}</td><td class="num">${money(c.lineTotal)}</td></tr>`);
      });
      if (fulfilment === 'ship') {
        rows.push(`<tr><td colspan="3">Shipping (NZ)</td><td class="num">${money(shipping)}</td></tr>`);
      }

      const how = {
        instore: '<b>Pay in store</b> when you collect from Camera House, 77 Queens Drive, Lower Hutt.',
        collect: '<b>Pay online now</b> — you\'ll go to our secure checkout, then collect from Camera House, 77 Queens Drive, Lower Hutt.',
        ship: '<b>Pay online now</b> — you\'ll enter your delivery address at checkout. We print once payment goes through.',
      }[fulfilment];

      el.dialogBody.innerHTML = `
        <div class="pnz-table-scroll"><table class="pnz-table">
          <thead><tr><th>Item</th><th class="num">Qty</th><th class="num">Each</th><th class="num">Line</th></tr></thead>
          <tbody>${rows.join('')}</tbody>
          <tfoot><tr><td colspan="3">Total</td><td class="num">${money(subtotal + shipping)}</td></tr></tfoot>
        </table></div>
        <p class="pnz-dialog__how">${how}</p>
      `;
    }

    // ─── Submit ───────────────────────────────────────────────────────────────
    // Each print line becomes its own payload entry sharing the photo's r2_key.
    // Prices use the SAME aggregated tier pricing the summary showed.
    function buildCompletePayloadFiles() {
      const globalFinish = el.finish.value;
      const { fixedGroups } = summarizeOrder();
      const out = [];
      uploadedFiles.forEach(file => {
        file.options.prints.forEach(line => {
          const isCustom = line.size === 'custom';
          const entry = {
            name: file.name,
            r2_key: file.r2Key || '',
            print_size: line.size,
            finish: isCustom ? line.poster.paper : hasPaperChoice(line.size) ? line.paper : globalFinish,
            qty: line.quantity,
          };
          if (isCustom) {
            entry.poster = {
              paper: line.poster.paper,
              height_cm: Number(line.poster.height_cm),
              width_cm: Number(line.poster.width_cm),
            };
            entry.price = calculatePosterUnitPrice(line.poster) * line.quantity;
          } else {
            const grp = fixedGroups[groupKey(line)];
            entry.price = (grp ? grp.unit : calculateTieredPrice(line.size, line.quantity, line.paper)) * line.quantity;
          }
          out.push(entry);
        });
      });
      return out;
    }

    el.submit.addEventListener('click', function () {
      showError('');
      if (!API_KEY) { showError('Online ordering isn\'t switched on yet — please bring your photos in store.'); return; }
      if (uploadedFiles.length === 0) { showError('Add at least one photo first.'); return; }
      if (!el.name.value.trim()) { showError('Please enter your name.'); el.name.focus(); return; }
      if (!el.email.value.trim() || !el.email.checkValidity()) { showError('Please enter a valid email address.'); el.email.focus(); return; }

      const invalidPosters = uploadedFiles.reduce((n, f) =>
        n + f.options.prints.filter(l => l.size === 'custom' && !isPosterSpecValid(l.poster)).length, 0);
      if (invalidPosters > 0) {
        showError(`${invalidPosters} custom poster size(s) are out of range — width ${POSTER_W_MIN_CM}–${POSTER_W_MAX_CM}cm, height ${POSTER_H_MIN_CM}–${POSTER_H_MAX_CM}cm.`);
        return;
      }
      const ongoing = ongoingCount();
      if (ongoing > 0) {
        showError(`${ongoing} photo(s) are still uploading — your order can go through as soon as they finish.`);
        return;
      }
      const failed = uploadedFiles.filter(f => f.uploadStatus === 'error').length;
      if (failed > 0) { showError(`${failed} photo(s) didn't upload. Remove them (×) and add them again.`); return; }
      if (!currentOrderId) { showError('Upload preparation failed. Please remove your photos and try again.'); return; }

      renderConfirm();
      if (typeof el.dialog.showModal === 'function') el.dialog.showModal();
      else placeOrderNow(); // very old browser — skip the confirmation step
    });

    el.dialogCancel.addEventListener('click', () => el.dialog.close());
    el.dialogConfirm.addEventListener('click', () => { el.dialog.close(); placeOrderNow(); });

    function setOverlay(on, text) {
      el.overlay.hidden = !on;
      el.overlayText.textContent = text || '';
    }

    function placeOrderNow() {
      setOverlay(true, 'Sending your order…');
      const payload = {
        order_id: currentOrderId,
        r2_prefix: currentR2Prefix,
        customer: { name: el.name.value.trim(), email: el.email.value.trim() },
        notes: el.notes.value.trim(),
        files: buildCompletePayloadFiles(),
        fulfilment: getFulfilment(),
      };

      function attemptComplete(attempt) {
        return apiCall('/print-orders/complete', payload).catch(err => {
          if (!isTransientError(err) || attempt >= COMPLETE_RETRY_DELAYS_MS.length) throw err;
          setOverlay(true, `Connection problem — retrying automatically (attempt ${attempt + 2} of ${COMPLETE_RETRY_DELAYS_MS.length + 1})…`);
          return new Promise(r => setTimeout(r, COMPLETE_RETRY_DELAYS_MS[attempt])).then(() => attemptComplete(attempt + 1));
        });
      }

      attemptComplete(0).then(data => {
        if (data && data.checkout_url) {
          // Clear the list first so the beforeunload guard doesn't block the redirect.
          setOverlay(true, 'Taking you to secure checkout…');
          uploadedFiles = [];
          window.location.href = data.checkout_url;
          return;
        }
        setOverlay(false);
        el.orderId.textContent = data.order_id || currentOrderId;
        uploadedFiles.forEach(f => f.thumbUrl && URL.revokeObjectURL(f.thumbUrl));
        resetState();
        el.form.hidden = true;
        el.done.hidden = false;
        el.done.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }).catch(error => {
        console.error('Order failed:', error);
        setOverlay(false);
        if (isTransientError(error)) {
          // Photos are safe in R2 and the form is kept — a later retry completes the same order.
          showError('We couldn\'t reach our server to finish your order. Your photos ARE uploaded — leave this page open and press the button again in a minute or two.');
        } else {
          showError('There was a problem placing your order: ' + error.message);
        }
      });
    }

    window.addEventListener('beforeunload', function (e) {
      if (uploadedFiles.length > 0) { e.preventDefault(); e.returnValue = ''; }
    });

    el.newOrder.addEventListener('click', function () {
      el.previews.innerHTML = '';
      el.notes.value = '';
      resetState();
      el.finish.value = 'glossy';
      const instore = root.querySelector('.pnz-fulfil input[value="instore"]');
      if (instore) instore.checked = true;
      el.more.hidden = true;
      updateOrderSummary();
      updateOverallProgress();
      el.done.hidden = true;
      el.form.hidden = false;
    });

    updateOrderSummary();
  }

  function boot() { document.querySelectorAll('[data-pnz-app]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
